// src/studio/components/MermaidView.jsx
//
// Renders a DIAGRAM section's content (markdown containing a ```mermaid
// fence — see SectionKind.DIAGRAM's docstring). The sanitizer fixes are
// carried over from rds_ui/pages/DesignProjectDetail.jsx's own
// MermaidDiagram: real syntax issues models produce even when told the
// rules (classDef rx:, em dashes) that mermaid.js's own error message
// doesn't explain.
import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import mermaid from "mermaid";
import { AlertCircle, Loader2 } from "lucide-react";

mermaid.initialize({ startOnLoad: false, theme: "neutral", securityLevel: "loose" });

function sanitize(code) {
  return code.replace(/,rx:\d+/g, "").replace(/—/g, "-");
}

function extractMermaidBlock(content) {
  const match = /```mermaid\s*([\s\S]*?)```/.exec(content || "");
  return match ? match[1].trim() : (content || "").trim();
}

export default function MermaidView({ content }) {
  const [result, setResult] = useState({ code: null, svg: "", error: false });
  const code = extractMermaidBlock(content);
  // Renders are async and keyed by `code`; a stale response for a section
  // the user has since navigated away from must not overwrite the current
  // one's result.
  const requestRef = useRef(0);

  useEffect(() => {
    if (!code) return;
    const requestId = ++requestRef.current;
    const id = `mermaid-${Math.random().toString(36).slice(2)}`;
    mermaid
      .render(id, sanitize(code))
      .then(({ svg: rendered }) => {
        if (requestRef.current !== requestId) return;
        const hasSyntaxError = /syntax error|parse error/i.test(rendered);
        setResult({ code, svg: hasSyntaxError ? "" : rendered, error: hasSyntaxError });
      })
      .catch(() => {
        if (requestRef.current !== requestId) return;
        setResult({ code, svg: "", error: true });
      });
  }, [code]);

  if (!content || !content.trim()) {
    return <p className="text-sm text-slate-400 italic">Nothing generated for this section yet.</p>;
  }

  const { svg, error } = result.code === code ? result : { svg: "", error: false };

  if (error) {
    return (
      <div>
        <p className="text-xs text-red-500 mb-2 flex items-center gap-1">
          <AlertCircle size={12} /> Diagram syntax error — showing the raw source. Regenerate to fix.
        </p>
        <pre className="bg-gray-900 text-emerald-400 rounded-xl p-4 text-xs font-mono overflow-x-auto whitespace-pre">
          {code}
        </pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="flex items-center gap-2 text-slate-400 py-6 text-xs">
        <Loader2 size={14} className="animate-spin" /> Rendering diagram…
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-x-auto p-4 bg-white">
      <div dangerouslySetInnerHTML={{ __html: svg }} className="flex justify-center" />
    </div>
  );
}

MermaidView.propTypes = { content: PropTypes.string };
